/**
 * * IMPORTANT:
 * This component is mounted to parent component in a way, that every time the tab changes,
 * the component gets destroyed and created when mounted. Reason the flag "isChecked & isDisabled"
 * has to change on each document & if multiple deal table concepts comes in it has to reset & update
 * the flags based on the current document & dealtable ID.
 */

// NPM Imports
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { connect } from "react-redux";
import { Toastr, Tooltip, Button } from "../../revvComponents";

// Components Imports
import ProductListing from "./ProductListing";
import SelectPriceBook from "./SelectPriceBook";
import { ConfirmationBox } from "../../commonComponents";

// Actions Imports
import { DealTableActions, QuotationActions } from "../../actions";
import { updateDynamicFieldsInDocForCatalog } from "../../actions/DocumentActions";

// Internal Imports
import { COMMON, EVENTS, LOADING_MSG } from "../../constants";
import { GlobalUtils, AmplitudeUtils } from "../../utils";
import LZLoader from "../../commonComponents/LZLoader";

// Destructuring
const { APP_FLAGS, CURRENCY_SYMBOL } = COMMON;
const { addProductsToDealTableV2, getCatalogData, updateProductCatalogs } =
  DealTableActions;
const { togglePriceBookSelectionView } = QuotationActions;
const { checkAppFlag, setProductPricebookCatalogClause } = GlobalUtils;

// Declarations
const DEFAULT_PRODUCT_TYPE = "All Product Types";
const IS_CHECKED = "isChecked";
const IS_DISABLED = "isDisabled";

class Catalog extends Component {
  state = {
    loader: false,
    loaderMsg: "",
    prodListingLoader: false,
    selectedProductIds: [],
    showPriceBookChangePopUp: false,
  };

  componentDidMount() {
    this._onInit();
  }

  /**
   * _onAllProductsSelect func stores the selected product id's
   * @param {number} productId - Holds product id
   */
  _onAllProductsSelect = (productId, isChecked) => {
    this.setState({ selectedProductIds: isChecked ? productId : [] }, () =>
      this.props.updateProductCatalogs(
        productId,
        isChecked,
        IS_CHECKED,
        this.props.productCatalogs,
      ),
    );
  };

  /**
   * _toggleCheckboxSelection func toggles is isChecked flag of a product
   * @param {number} productId - Holds product id
   * @param {Boolean} isChecked - Holds true/false value of isChecked property
   */
  _toggleCheckboxSelection = (productId, isChecked) => {
    if (isChecked) {
      this.setState(prevState => ({
        selectedProductIds: prevState.selectedProductIds.concat(productId),
      }));
    } else {
      this.setState(prevState => ({
        selectedProductIds: prevState.selectedProductIds.filter(
          item => item !== productId,
        ),
      }));
    }
    this.props.updateProductCatalogs(
      [productId],
      isChecked,
      IS_CHECKED,
      this.props.productCatalogs,
    );
  };

  /**
   * _addItemsToDealTable func allows to add product from catalog search into dealtable
   */
  _addItemsToDealTable = () => {
    // const selectedProducts = this.props.productCatalogs
    //   .filter(item => item.isChecked && !item.isDisabled)
    //   .map(item => item.id);
    const selectedProducts = [...new Set(this.state.selectedProductIds)];
    this.setState(
      { loader: true, loaderMsg: LOADING_MSG.ADD_ITEM_QTABLE },
      () => {
        this.props
          .addProductsToDealTableV2(
            selectedProducts,
            true,
            IS_DISABLED,
            this.props.productCatalogs,
            this.props.dealTableId,
            this.props.pricelistId,
            this.props.documentId,
          )
          .then(() => {
            const newdata = setProductPricebookCatalogClause(
              this.props.fields_used,
              this.props.addedCatalogIds,
              this.props.productCatalogs,
              this.props.docComponents,
            );
            const finalValue = [];
            this.props.fields_used.forEach(item => {
              newdata.forEach(value => {
                if (item.id === value.id) {
                  finalValue.push(value);
                } else {
                  finalValue.push(item);
                }
              });
            });

            this.props.updateDynamicFieldsInDocForCatalog(
              this.props.documentId,
              newdata,
            );
            this.setState({
              loader: false,
              loaderMsg: "",
              selectedProductIds: [],
            });
            AmplitudeUtils.amplitudeLogEvent(EVENTS.EVENT_NAMES.PRODUCT_ADDED, {
              ProductAddedCount: selectedProducts.length,
            });
          })
          .catch(error => {
            this.setState({
              loader: false,
              loaderMsg: "",
            });
            Toastr.showToastr({
              type: "failure",
              message: "Oops! Something went wrong. Please try again later.",
              timeout: 3000,
            });
            console.error("Catalog => _addItemsToDealTable", error);
          });
      },
    );
  };

  /**
   * _onCatalogSearch func is a prop callback when product is searched in search box
   */
  _onCatalogSearch = (searchTxt, productType) => {
    const { pricelistId, addedCatalogIds } = this.props;
    this.props.getCatalogData(
      pricelistId,
      searchTxt,
      productType,
      addedCatalogIds || [],
    );
  };

  /**
   * _onPriceBookUpdateClick func decides to show a confirmation screen or show available pricebook based
   * on if dealtable has rows added to it or not.
   */
  _onPriceBookUpdateClick = () => {
    if (this.props.hasDealTableRows) {
      this.setState({ showPriceBookChangePopUp: true });
    } else {
      this._onPriceBookChange();
    }
  };

  /**
   * _onPriceBookChange func allows to change pricebook associated to a dealtable
   */
  _onPriceBookChange = () => {
    this.props.togglePriceBookSelectionView();
  };

  _onInit = () => {
    const { pricelistId, addedCatalogIds } = this.props;
    this.setState({ prodListingLoader: true });
    this.props
      .getCatalogData(
        pricelistId,
        "",
        DEFAULT_PRODUCT_TYPE,
        addedCatalogIds || [],
      )
      .then(() => {
        this.setState({ prodListingLoader: false });
      })
      .catch(error => {
        console.error("Catalog => _onInit", error);
        this.setState({ prodListingLoader: false });
      });
  };

  render() {
    if (this.state.loader) {
      return <LZLoader label={this.state.loaderMsg} fullScreen />;
    }
    const { selectedProductIds } = this.state;
    const hideDealDiscount = checkAppFlag(APP_FLAGS.DEAL_DISCOUNT); // Flags to display UI components based on Org setting
    return (
      <Fragment>
        {this.props.showPriceBook && (
          <SelectPriceBook
            isPriceBookAttached={this.props.isPriceBookAttached}
          />
        )}
        {!this.props.showPriceBook && (
          <div
            className={`catalog-wrapper animated fadeinright ${
              hideDealDiscount ? "is-small" : ""
            }`}>
            {/* Section to show PriceBook Name, Edit PriceBook & Add To DealTable Button */}
            <section>
              <div className="catalog-title-section">
                <div className="catalog-title">
                  Catalog:
                  <span className="catalog-currency-title">
                    {this.props.currencyName}
                  </span>
                  <Tooltip
                    placement="bottom"
                    overlay="Change Price Book"
                    align={{
                      offset: [0, 2],
                    }}>
                    <i
                      className="catalog-pricebook-edit"
                      onClick={this._onPriceBookUpdateClick}
                      onKeyPress={() => {}}
                      role="button"
                      tabIndex="0">
                      <FontAwesomeIcon icon={["far", "edit"]} />
                    </i>
                  </Tooltip>
                </div>
                <Button
                  className="productlisting-addproducts-btn"
                  data-test="add-to-quote-table-button"
                  onClick={this._addItemsToDealTable}
                  disabled={selectedProductIds.length === 0}>
                  {" "}
                  Add to quote table(
                  {selectedProductIds.length})
                </Button>
              </div>
            </section>

            {/* Section Displaying Products Based on the selected PriceBook */}
            <section className="catalog-container">
              <div className="catalog-contents">
                <ProductListing
                  addItemsToDealTable={this._addItemsToDealTable}
                  catalogItems={this.props.productCatalogs}
                  currencySymbol={CURRENCY_SYMBOL[this.props.currency]}
                  loader={this.state.prodListingLoader}
                  onAllProductsSelect={this._onAllProductsSelect}
                  onCatalogSearch={this._onCatalogSearch}
                  selectedProducts={this.state.selectedProductIds}
                  toggleCheckboxSelection={this._toggleCheckboxSelection}
                />
              </div>
            </section>
            {/* Section to show confirmation msg when user wants to change pricebook if some rows are added to dealtable */}
            <section>
              <ConfirmationBox
                show={this.state.showPriceBookChangePopUp}
                closePopup={() =>
                  this.setState({ showPriceBookChangePopUp: false })
                }
                header="Are you sure you want to change Price Book?"
                subHeader="Changing pricebook will result in removing of all the products you added to deal table from this price book"
                checkboxLabel="Yes, I want to change"
                confirmAction={this._onPriceBookChange}
                okBtnText="Change Price Book"
              />
            </section>
          </div>
        )}
      </Fragment>
    );
  }
}

Catalog.propTypes = {
  // Props
  addedCatalogIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  dealTableId: PropTypes.number.isRequired,
  pricelistId: PropTypes.number.isRequired,
  productCatalogs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  // Actions
  addProductsToDealTableV2: PropTypes.func.isRequired,
  getCatalogData: PropTypes.func.isRequired,
  updateProductCatalogs: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  // ::DOCUMENT STORE
  const {
    document: { id: documentId, fields_used } = {},
    components: docComponents,
  } = state.DocumentStore;

  // ::QUOTATION STORE
  const { dealTableId, showPriceBook } = state.QuotationStore;

  // ::DEALTABLE STORE
  const {
    columnDefs: { [dealTableId]: columnDefsSet = [] } = {},
    productCatalogs = [],
    addedCatalogIds: { [dealTableId]: addedCatalogIdsSet } = [],
    rowData: { [dealTableId]: rowData = [] } = [],
    priceBook: { [dealTableId]: priceBook = {} } = {},
  } = state.DealTableStore;

  // ::ASSIGN RESPECTIVE DECLARED VALUES
  const addedCatalogIds = addedCatalogIdsSet || [];
  const currency = (priceBook && priceBook.currency) || "USD";
  const currencyName = (priceBook && priceBook.name) || "USD";
  const hasDealTableRows = rowData && rowData.length > 0;
  const isPriceBookAttached = columnDefsSet && columnDefsSet.length > 0; // Using column def we can identify if doc is new or already has pricebook attached
  const pricelistId = (priceBook && priceBook.id) || 0;

  return {
    addedCatalogIds, // ReadOnly Data
    currency, // ReadOnly Data
    currencyName, // ReadOnly Data
    dealTableId, // ReadOnly Data
    documentId, // ReadOnly Data
    hasDealTableRows, // ReadOnly Data
    isPriceBookAttached, // ReadOnly Data
    pricelistId, // ReadOnly Data
    productCatalogs,
    showPriceBook, // ReadOnly Data
    fields_used,
    docComponents,
  };
};

const mapDispatchToProps = {
  addProductsToDealTableV2, // props to be passed (productId, flag, flagType, productCatalogs, dealTableId)
  getCatalogData, // props to be passed (pricelistID, searchQuery, productType, addedCatalogIds)
  togglePriceBookSelectionView,
  updateProductCatalogs, // props to be passed (productId, flag, flagType, productCatalogs)
  updateDynamicFieldsInDocForCatalog,
};

export default connect(mapStateToProps, mapDispatchToProps)(Catalog);
